import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ShortUniqueId from 'short-unique-id'

import { Notice } from './components'
import { DEFAULT_SCOPE, registerContainer, unregisterContainer } from './helper'
import * as SC from './styles'
import type { Property, ToastCreator, ToastUpdater } from './types'

interface NoticeType extends Property {
    key: string
    content: string
}

interface NoticeStateType {
    notices: NoticeType
}

export const ToastContainer: React.FC = () => {
    const scope = DEFAULT_SCOPE
    const instanceRef = useRef<{ create: ToastCreator; update: ToastUpdater; close: (key?: string) => void } | null>(null)
    const [state, setState] = useState<NoticeStateType>({
        notices: {} as NoticeType
    })
    const {
        key: noticeKey,
        content,
        iconColor,
        spinning,
        icon,
        delayed,
        linkText,
        onLinkClick,
        style,
        autoClose,
        withPortal
    } = state.notices
    const create: ToastCreator = useCallback(
        (content, PropertyType) => {
            const keyId: string = new ShortUniqueId().stamp(11)
            setState({
                notices: {
                    key: keyId,
                    content,
                    ...PropertyType
                }
            })
        },
        [setState]
    )

    const update: ToastUpdater = useCallback((content: string, PropertyType?: Property) => {
        setState(prevState => ({
            notices: {
                ...prevState.notices, // 保留所有原有属性
                content, // 更新内容
                ...PropertyType // 覆盖指定的新属性
            }
        }))
    }, [])

    const close = useCallback(
        (keyId?: string) => {
            setState({ notices: {} as NoticeType })
        },
        [setState]
    )

    useEffect(() => {
        instanceRef.current = { create, update, close }
    })

    useEffect(() => {
        registerContainer(scope, {
            ...instanceRef.current!
        })

        return () => {
            unregisterContainer(scope)
        }
    }, [scope])

    const renderToast = useMemo(() => {
        const key = noticeKey
        const withPortalValue = withPortal ?? true
        const container = (
            <SC.Container>
                <Notice
                    key={noticeKey}
                    noticeKey={noticeKey}
                    content={content}
                    iconColor={iconColor}
                    spinning={spinning}
                    icon={icon}
                    delayed={delayed}
                    linkText={linkText}
                    onLinkClick={onLinkClick}
                    style={style}
                    autoClose={autoClose}
                    onClose={close}
                />
            </SC.Container>
        )
        return key ? (withPortalValue ? createPortal(container, document.body, key) : container) : null
    }, [noticeKey, withPortal, content, iconColor, spinning, icon, delayed, linkText, onLinkClick, style, autoClose, close])

    return <>{renderToast}</>
}
